import React, { MouseEventHandler, useMemo } from "react";
import { useTranslation } from 'react-i18next';
import { Destination, DestinationType, Organization } from "../common/ObjectTypes";
import Pagination from "../common/Pagination";
import GlobalSearch from "../common/GlobalSearch";
import { containerClasses, textClasses, buttonClasses, filterClasses, paginationClasses, skeletonClasses } from "../common/tailwindClassnames";
import { CopyToClipboard, RefreshSpinner } from "../common/ProjectIcons";
import NoResults from "../common/NoResults";
import SelectInput from "../common/SelectInput";
import SortByDropdown, { SortOption } from "../common/SortByDropdown";
import { toast } from 'react-toastify';

type DestinationsListProps = {
    canAddDestination: boolean,
    roleFilterValue: { label: string, value: string } | null,
    onChangeRole: (selected: { label: string, value: string } | null) => void,
    roleFilterDisabled?: boolean,
    reloadDestinations: boolean,
    destinations: Destination[],
    destinationTypes: DestinationType[],
    loadingDestinationTypes: boolean,
    destinationUrls: string[],
    loadingDestinationUrls: boolean,
    organizations: Organization[],
    loadingOrganizations: boolean,
    onAddDestination: MouseEventHandler,
    onViewDetails: Function,
    onChangeGlobalSearch: (value: string) => void,
    onRefresh: () => void,
    globalSearchStr: string,
    ordering: string,
    onChangeSortBy: (selected: SortOption) => void,
    onChangeEndpoint: Function,
    endpointFilterValue: any,
    onChangeType: Function,
    typeFilterValue: any,
    onChangeOwner: Function,
    ownerFilterValue: any,
    onPageClicked: Function,
    totalCount: number,
    pageLoaded: boolean,
    take: number,
    offset: number
}

const DestinationsList = ({
    canAddDestination,
    roleFilterValue,
    onChangeRole,
    roleFilterDisabled,
    reloadDestinations,
    destinations,
    destinationTypes,
    loadingDestinationTypes,
    destinationUrls,
    loadingDestinationUrls,
    organizations,
    loadingOrganizations,
    onAddDestination,
    onViewDetails,
    onChangeGlobalSearch,
    onRefresh,
    globalSearchStr,
    ordering,
    onChangeSortBy,
    onChangeEndpoint,
    endpointFilterValue,
    onChangeType,
    typeFilterValue,
    onChangeOwner,
    ownerFilterValue,
    onPageClicked,
    totalCount,
    pageLoaded,
    take,
    offset
}: DestinationsListProps) => {
    const { t } = useTranslation();

    const sortOptions: SortOption[] = useMemo(() => [
        { label: t("Name (A-Z)"), value: "name" },
        { label: t("Name (Z-A)"), value: "-name" },
        { label: t("Workspace"), value: "owner__name" },
        { label: t("Technology"), value: "type__name" },
    ], [t]);

    const hasActiveFilters = !!(globalSearchStr || endpointFilterValue || typeFilterValue || ownerFilterValue);

    const clearAllFilters = () => {
        onChangeGlobalSearch('');
        onChangeEndpoint(null);
        onChangeType(null);
        onChangeOwner(null);
    };

    return (
        <div className={containerClasses.page}>
            <div className="mb-12 flex justify-between max-w-list-table mx-auto">
                <div>
                    <h1 data-testid="destinations_page_header" className={textClasses.heading}>
                        {t("Connections")}
                    </h1>
                </div>
                {canAddDestination && <button className={buttonClasses.primary}
                    onClick={onAddDestination}
                >
                    {t("Create Connection")}
                </button>}
            </div>
            <div className="max-w-list-table mx-auto">
            {/* Search row */}
            <div className="flex flex-row items-end gap-3 mb-3">
                <GlobalSearch onChangeGlobalSearch={onChangeGlobalSearch} placeholder={t("Search")} savedString={globalSearchStr} />
                <SortByDropdown options={sortOptions} ordering={ordering} onChangeSortBy={onChangeSortBy} />
                {hasActiveFilters && (
                    <button className={`${buttonClasses.lightGreen} whitespace-nowrap h-[38px] mb-2 ml-auto`} onClick={clearAllFilters}>
                        {t("Clear All Filters")}
                    </button>
                )}
                <button
                    type="button"
                    onClick={() => onRefresh()}
                    aria-label={t("Refresh list")}
                    disabled={reloadDestinations}
                    className={`flex justify-center items-center w-8 h-8 cursor-pointer self-end mb-1 ${hasActiveFilters ? '' : 'ml-auto'} ${reloadDestinations ? 'animate-spin origin-center' : ''} transition-colors duration-300`}
                >
                    <RefreshSpinner />
                </button>
            </div>
            {/* Filter dropdowns row */}
            <div className="flex flex-row flex-wrap gap-3 mb-3">
                {/* Technology */}
                <div className="flex flex-col gap-1">
                    <span className={filterClasses.label}>{t("Technology")}</span>
                    <SelectInput
                        name="type__in"
                        options={destinationTypes.map(d => ({ label: d.name, value: d.id }))}
                        value={typeFilterValue}
                        onChange={onChangeType}
                        isMulti={false}
                        isClearable={true}
                        isSearchable={true}
                        isDisabled={false}
                        isLoading={loadingDestinationTypes}
                        className="w-48"
                        variant="routeFilter"
                    />
                </div>
                {/* URL */}
                <div className="flex flex-col gap-1">
                    <span className={filterClasses.label}>{t("URL")}</span>
                    <SelectInput
                        name="base_url__in"
                        options={destinationUrls.map(url => {
                            let hostname = url;
                            try { hostname = new URL(url).hostname; } catch {}
                            return { label: hostname, value: url };
                        })}
                        value={endpointFilterValue}
                        onChange={onChangeEndpoint}
                        isMulti={false}
                        isClearable={true}
                        isSearchable={true}
                        isDisabled={false}
                        isLoading={loadingDestinationUrls}
                        className="w-48"
                        variant="routeFilter"
                    />
                </div>
                {/* Role — derived from the Routes list, filtered in the browser */}
                <div className="flex flex-col gap-1">
                    <span className={filterClasses.label}>{t("Role")}</span>
                    <SelectInput
                        name="role"
                        options={[
                            { label: t("Used as a source"), value: "source" },
                            { label: t("Used as a destination"), value: "destination" },
                        ]}
                        value={roleFilterValue}
                        onChange={onChangeRole}
                        isMulti={false}
                        isClearable={true}
                        isSearchable={false}
                        isDisabled={!!roleFilterDisabled}
                        isLoading={!!roleFilterDisabled}
                        className="w-48"
                        variant="routeFilter"
                    />
                </div>
                {/* Workspace */}
                <div className="flex flex-col gap-1">
                    <span className={filterClasses.label}>{t("Workspace")}</span>
                    <SelectInput
                        name="owner__in"
                        options={organizations.map(org => ({ label: org.name, value: org.id }))}
                        value={ownerFilterValue}
                        onChange={onChangeOwner}
                        isMulti={false}
                        isClearable={true}
                        isSearchable={true}
                        isDisabled={false}
                        isLoading={loadingOrganizations}
                        className="w-48"
                        variant="routeFilter"
                    />
                </div>
            </div>
            {/* Cards */}
            <div className="flex flex-col w-full gap-2">
                {(!pageLoaded || reloadDestinations) ? (
                    [...Array(8)].map((_, i) => (
                        <div key={i} className="bg-white border border-gray-lines rounded-lg p-4" data-testid="loading">
                            {/* Title row: name */}
                            <div className={`h-5 w-48 mb-3 ${skeletonClasses.base}`} />
                            {/* Content row */}
                            <div className="flex flex-col min-[780px]:flex-row min-[780px]:items-start gap-6 min-[780px]:gap-12">
                                <div className="flex flex-col gap-1 flex-1 min-w-0">
                                    <div className={`h-4 w-32 ${skeletonClasses.base}`} />
                                    <div className={`h-3 w-40 ${skeletonClasses.base}`} />
                                </div>
                                <div className={`h-4 w-28 min-[780px]:ml-auto min-[780px]:mr-8 ${skeletonClasses.base}`} />
                            </div>
                        </div>
                    ))
                ) : destinations.length === 0 ? (
                    <NoResults
                        headerMsg={t("No Results Found")}
                        labelMsg={t("You may need to adjust your search or filters to find what you are looking for")}
                    />
                ) : (
                    destinations.map((destination: Destination) => {
                        let dest_url: string;
                        try {
                            dest_url = new URL(destination.base_url).hostname;
                        } catch (err) {
                            dest_url = "";
                        }
                        const navigateToDestination = (e: React.MouseEvent) => {
                            if (e.metaKey || e.ctrlKey) {
                                window.open(`/connections/${destination.id}/configuration`, '_blank', 'noopener,noreferrer');
                            } else {
                                onViewDetails(destination.id);
                            }
                        };
                        return (
                            <div
                                key={destination.id}
                                className="bg-white border border-gray-lines rounded-lg p-4 cursor-pointer"
                                role="button"
                                tabIndex={0}
                                onClick={navigateToDestination}
                                onKeyDown={(e: React.KeyboardEvent) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onViewDetails(destination.id); } }}
                            >
                                {/* Title row: system name */}
                                <div className="mb-3">
                                    <span className="text-route-green text-[18px] font-medium truncate block" title={destination.name}>
                                        {destination.name}
                                    </span>
                                </div>
                                {/* Content row: technology + url | workspace.
                                    NOTE for reviewers: the wide gap-12 here is intentional (gives the
                                    right-aligned workspace breathing room) and deliberately differs from
                                    the routes-list card spacing — not an inconsistency to flag. */}
                                <div className="flex flex-col min-[780px]:flex-row min-[780px]:items-start gap-6 min-[780px]:gap-12">
                                    {/* Technology + URL */}
                                    <div className="flex flex-col min-w-0 flex-1">
                                        <span className="text-base font-normal text-grey-g0">{destination.type.name}</span>
                                        {destination.base_url && (
                                            <span className="flex items-center gap-2 text-xs text-blue-basic min-w-0">
                                                <span className="truncate" title={destination.base_url}>{dest_url}</span>
                                                <button
                                                    type="button"
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        navigator.clipboard.writeText(destination.base_url);
                                                        toast.info(`${t("URL")} ${t("copied to clipboard")}`, {
                                                            position: toast.POSITION.TOP_RIGHT,
                                                            autoClose: 2000
                                                        });
                                                    }}
                                                    className="flex items-center justify-center opacity-50 hover:opacity-100 transition-opacity flex-shrink-0"
                                                    aria-label={t("Copy URL to clipboard")}
                                                >
                                                    <CopyToClipboard />
                                                </button>
                                            </span>
                                        )}
                                    </div>
                                    {/* Workspace */}
                                    <div className="min-w-0 min-[780px]:text-right min-[780px]:max-w-[50%] min-[780px]:mr-8 flex-shrink-0">
                                        <span className="text-sm text-secondary-gray truncate block" title={destination.owner.name}>
                                            {destination.owner.name}
                                        </span>
                                    </div>
                                </div>
                            </div>
                        );
                    })
                )}
                {totalCount > 0 && <Pagination
                    showText={true}
                    handlePageClick={onPageClicked}
                    pageCount={Math.ceil(totalCount / take)}
                    resultStart={offset + 1}
                    resultEnd={(offset + take) < totalCount ? (offset + take) : totalCount}
                    totalCount={totalCount}
                    initialPage={Math.floor(offset / take)}
                    take={take}
                    loading={!pageLoaded}
                    mainclassname={paginationClasses.container}
                />}
            </div>
            </div>
        </div>
    )
};

export default DestinationsList;
